import { useState, useCallback, useMemo } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/ui/status-badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  Upload,
  FileText,
  Sparkles,
  Trash2,
  Archive,
  Inbox,
  Clock,
  Eye,
  Download,
  MoreVertical,
  AlertCircle,
  CheckCircle2,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Sheet,
  Image,
  File,
  Building2,
} from "lucide-react";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/useMobile";
import { useCompany } from "@/contexts/CompanyContext";
import DashboardLayout from "@/components/DashboardLayout";
import { AnalysisReviewModal } from "@/components/AnalysisReviewModal";
import { CSVPreview } from "@/components/CSVPreview";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type FolderType = "inbox" | "processing" | "archived" | "deleted";
type SortColumn = "name" | "type" | "date" | "size";
type SortDirection = "asc" | "desc" | null;

interface Filters {
  type?: string;
  size?: string;
  date?: string;
  search?: string;
}

export default function FileInbox() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { selectedCompanyId } = useCompany();
  const utils = trpc.useUtils();
  const { data: companies = [] } = trpc.company.list.useQuery(undefined, {
    enabled: !!user,
  });
  const selectedCompany = companies.find(c => c.id === selectedCompanyId);

  // State
  const [activeFolder, setActiveFolder] = useState<FolderType>("inbox");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState<Set<number>>(new Set());
  const [sortColumn, setSortColumn] = useState<SortColumn>("date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [filters, setFilters] = useState<Filters>({});
  const [selectedDocForAnalysis, setSelectedDocForAnalysis] = useState<
    number | null
  >(null);
  const [analysisModalOpen, setAnalysisModalOpen] = useState(false);
  const [csvPreviewDoc, setCsvPreviewDoc] = useState<{
    id: number;
    url: string;
    filename: string;
  } | null>(null);
  const [csvPreviewOpen, setCsvPreviewOpen] = useState(false);
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState<{
    message: string;
    ids: number[];
  } | null>(null);

  // Duplicate file detection state (gt-c3i34)
  const [duplicateWarning, setDuplicateWarning] = useState<{
    duplicates: string[];
    pendingFiles: File[];
  } | null>(null);

  // Queries
  const {
    data: documents,
    isLoading,
    refetch,
  } = trpc.documentManagement.listByFolder.useQuery(
    {
      companyId: selectedCompanyId,
      folder: activeFolder,
      limit: 50,
      offset: 0,
    },
    { enabled: !!user && !!selectedCompanyId }
  );

  const { data: folderStats } = trpc.documentManagement.getFolderStats.useQuery(
    { companyId: selectedCompanyId },
    { enabled: !!user && !!selectedCompanyId }
  );

  const { data: analysisData } = trpc.documentManagement.getAnalysis.useQuery(
    { documentId: selectedDocForAnalysis! },
    { enabled: !!selectedDocForAnalysis && !!selectedCompanyId }
  );

  // Mutations
  const createDocument = trpc.documentManagement.create.useMutation();
  const moveToFolder = trpc.documentManagement.moveToFolder.useMutation({
    onSuccess: () => {
      // Invalidate both the source folder and target folder queries
      utils.documentManagement.listByFolder.invalidate();
      utils.documentManagement.getFolderStats.invalidate();
    },
    onError: error => {
      toast.error(`Failed to move document: ${error.message}`);
    },
  });
  const analyzeDocument = trpc.documentManagement.analyze.useMutation({
    onSuccess: () => {
      utils.documentManagement.listByFolder.invalidate();
      utils.documentManagement.getAnalysis.invalidate();
    },
    onError: error => {
      toast.error(`Failed to analyze document: ${error.message}`);
    },
  });
  const deletePermanently =
    trpc.documentManagement.deletePermanently.useMutation({
      onSuccess: () => {
        utils.documentManagement.listByFolder.invalidate();
        utils.documentManagement.getFolderStats.invalidate();
      },
      onError: error => {
        toast.error(`Failed to delete document: ${error.message}`);
      },
    });

  const createVendor = trpc.vendorIntelligence.upsertVendor.useMutation({
    onSuccess: () => {
      utils.vendorIntelligence.listVendors.invalidate();
      toast.success("Vendor created successfully");
    },
    onError: error => {
      toast.error(`Failed to create vendor: ${error.message}`);
    },
  });

  const createTransaction = trpc.transaction.create.useMutation({
    onSuccess: () => {
      utils.transaction.list.invalidate();
      toast.success("Transaction created successfully");
    },
    onError: error => {
      toast.error(`Failed to create transaction: ${error.message}`);
    },
  });

  // Filter and sort documents
  const filteredAndSortedDocs = useMemo(() => {
    if (!documents) return [];

    let filtered = [...documents];

    // Apply filters
    if (filters.type && filters.type !== "all") {
      filtered = filtered.filter(doc => doc.documentType === filters.type);
    }

    if (filters.size) {
      filtered = filtered.filter(doc => {
        if (filters.size === "small") return doc.fileSize < 100 * 1024;
        if (filters.size === "medium")
          return doc.fileSize >= 100 * 1024 && doc.fileSize < 1024 * 1024;
        if (filters.size === "large") return doc.fileSize >= 1024 * 1024;
        return true;
      });
    }

    if (filters.date) {
      const now = Date.now();
      const dayMs = 24 * 60 * 60 * 1000;
      filtered = filtered.filter(doc => {
        const uploadedAtMs = doc.uploadedAt.getTime();
        if (filters.date === "today") return now - uploadedAtMs < dayMs;
        if (filters.date === "7days") return now - uploadedAtMs < 7 * dayMs;
        if (filters.date === "30days") return now - uploadedAtMs < 30 * dayMs;
        if (filters.date === "90days") return now - uploadedAtMs < 90 * dayMs;
        return true;
      });
    }

    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(
        doc =>
          doc.fileName.toLowerCase().includes(search) ||
          doc.originalName?.toLowerCase().includes(search)
      );
    }

    // Apply sorting
    if (sortColumn && sortDirection) {
      filtered.sort((a, b) => {
        let aVal: any, bVal: any;

        switch (sortColumn) {
          case "name":
            aVal = (a.originalName || a.fileName).toLowerCase();
            bVal = (b.originalName || b.fileName).toLowerCase();
            break;
          case "type":
            aVal = a.documentType || "";
            bVal = b.documentType || "";
            break;
          case "date":
            aVal = a.uploadedAt;
            bVal = b.uploadedAt;
            break;
          case "size":
            aVal = a.fileSize;
            bVal = b.fileSize;
            break;
          default:
            return 0;
        }

        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [documents, filters, sortColumn, sortDirection]);

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedDocIds.size === filteredAndSortedDocs.length) {
      setSelectedDocIds(new Set());
    } else {
      setSelectedDocIds(new Set(filteredAndSortedDocs.map(d => d.id)));
    }
  };

  const toggleSelect = (id: number) => {
    const newSelected = new Set(selectedDocIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedDocIds(newSelected);
  };

  // Sort handlers
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      if (sortDirection === "asc") setSortDirection("desc");
      else if (sortDirection === "desc") {
        setSortDirection(null);
        setSortColumn("date");
      }
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column)
      return <ArrowUpDown className="h-3 w-3 opacity-50" />;
    if (sortDirection === "asc") return <ArrowUp className="h-3 w-3" />;
    if (sortDirection === "desc") return <ArrowDown className="h-3 w-3" />;
    return <ArrowUpDown className="h-3 w-3 opacity-50" />;
  };

  // File upload handlers

  // Check for duplicate file names against existing documents (gt-c3i34)
  const checkAndSetFiles = (files: File[]) => {
    const existingNames = new Set(
      (documents || []).map(d => d.fileName.toLowerCase())
    );
    const dups = files
      .map(f => f.name)
      .filter(n => existingNames.has(n.toLowerCase()));
    if (dups.length > 0) {
      setDuplicateWarning({ duplicates: dups, pendingFiles: files });
    } else {
      setSelectedFiles(files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      checkAndSetFiles(Array.from(e.target.files));
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        checkAndSetFiles(Array.from(e.dataTransfer.files));
      }
    },
    [documents] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    if (!selectedCompanyId) {
      toast.error("Please select a company first");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const reader = new FileReader();
        const fileData = await new Promise<string>(resolve => {
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        });

        const result = await createDocument.mutateAsync({
          companyId: selectedCompanyId,
          filename: file.name,
          fileSize: file.size,
          mimeType: file.type,
          folder: "inbox",
          fileData,
        });

        if ((result as any).isDuplicate) {
          const dup = result as any;
          toast.warning(
            `"${dup.fileName}" was already uploaded on ${new Date(dup.uploadedAt).toLocaleDateString()}. Skipped.`
          );
        }

        setUploadProgress(Math.round(((i + 1) / selectedFiles.length) * 100));
      }

      toast.success(`Uploaded ${selectedFiles.length} file(s) to Inbox`);
      setSelectedFiles([]);
      refetch();
    } catch (error) {
      toast.error("Failed to upload files");
      console.error(error);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Batch actions
  const handleBatchMove = async (targetFolder: FolderType) => {
    if (!selectedCompanyId || selectedDocIds.size === 0) return;

    // Filter out documents that don't exist in the current document list (phantom documents)
    const validDocIds = Array.from(selectedDocIds).filter(docId =>
      documents?.some(doc => doc.id === docId)
    );

    if (validDocIds.length === 0) {
      toast.error("No valid documents selected. Try refreshing the page.");
      setSelectedDocIds(new Set());
      return;
    }

    if (validDocIds.length < selectedDocIds.size) {
      toast.warning(
        `${selectedDocIds.size - validDocIds.length} phantom document(s) detected and skipped. Click "Force Refresh" to clear cache.`
      );
    }

    const count = validDocIds.length;
    const loadingToast = toast.loading(
      `Moving ${count} document${count > 1 ? "s" : ""}...`
    );

    try {
      // Move documents sequentially to avoid overwhelming the server
      for (const docId of validDocIds) {
        await moveToFolder.mutateAsync({
          documentId: docId,
          companyId: selectedCompanyId,
          targetFolder,
        });
      }

      toast.dismiss(loadingToast);
      toast.success(
        `Successfully moved ${count} document${count > 1 ? "s" : ""} to ${targetFolder}`
      );
      setSelectedDocIds(new Set());
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(
        `Failed to move documents: ${error instanceof Error ? error.message : "Unknown error"}`
      );
      console.error("[handleBatchMove] Error:", error);
    }
  };

  const handleBatchDelete = async () => {
    if (!selectedCompanyId || selectedDocIds.size === 0) return;

    // Filter out phantom documents
    const validDocIds = Array.from(selectedDocIds).filter(docId =>
      documents?.some(doc => doc.id === docId)
    );

    if (validDocIds.length === 0) {
      toast.error("No valid documents selected. Try refreshing the page.");
      setSelectedDocIds(new Set());
      return;
    }

    if (validDocIds.length < selectedDocIds.size) {
      toast.warning(
        `${selectedDocIds.size - validDocIds.length} phantom document(s) detected and skipped. Click "Force Refresh" to clear cache.`
      );
    }

    const confirmMessage =
      activeFolder === "deleted"
        ? `Permanently delete ${validDocIds.length} document(s)? This cannot be undone.`
        : `Delete ${validDocIds.length} document(s)? They will be moved to Deleted folder.`;

    setBulkDeleteDialog({ message: confirmMessage, ids: validDocIds });
  };

  const executeBatchDelete = async (ids: number[]) => {
    if (!selectedCompanyId) return;
    try {
      for (const docId of ids) {
        if (activeFolder === "deleted") {
          await deletePermanently.mutateAsync({
            documentId: docId,
            companyId: selectedCompanyId,
          });
        } else {
          await moveToFolder.mutateAsync({
            documentId: docId,
            companyId: selectedCompanyId,
            targetFolder: "deleted",
          });
        }
      }
      toast.success(`Deleted ${ids.length} document(s)`);
      setSelectedDocIds(new Set());
      refetch();
    } catch (error) {
      toast.error("Failed to delete documents");
    }
  };

  // Document actions
  const handleAnalyze = async (documentId: number) => {
    if (!selectedCompanyId) {
      toast.error("Please select a company first");
      return;
    }
    try {
      await analyzeDocument.mutateAsync({
        documentId,
        companyId: selectedCompanyId,
      });
      toast.success("Document analyzed successfully");
      refetch();
    } catch (error) {
      toast.error("Failed to analyze document");
    }
  };

  // Utility functions
  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes("pdf"))
      return <FileText className="h-5 w-5 text-destructive" />;
    if (mimeType.includes("csv"))
      return <Sheet className="h-5 w-5 text-[var(--success)]" />;
    if (mimeType.includes("excel") || mimeType.includes("spreadsheet"))
      return <Sheet className="h-5 w-5 text-primary" />;
    if (mimeType.includes("image"))
      return <Image className="h-5 w-5 text-[var(--info)]" />;
    return <File className="h-5 w-5 text-muted-foreground" />;
  };

  const getDocumentTypeColor = (type?: string) => {
    switch (type) {
      case "invoice":
        return "bg-primary";
      case "receipt":
        return "bg-[var(--success)]";
      case "contract":
        return "bg-accent";
      case "bank_statement":
        return "bg-[var(--warning)]";
      case "tax_form":
        return "bg-destructive";
      default:
        return "bg-muted-foreground";
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("de-CH", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const activeFilters = Object.entries(filters).filter(
    ([_, value]) => value && value !== "all"
  );

  // Force refresh handler - clears all caches and refetches data
  const handleForceRefresh = useCallback(async () => {
    const loadingToast = toast.loading("Clearing cache and refreshing data...");
    try {
      // Clear all documentManagement queries from cache
      await utils.documentManagement.invalidate();
      // Also clear any selected state
      setSelectedDocIds(new Set());
      // Force refetch
      await refetch();
      toast.dismiss(loadingToast);
      toast.success("Cache cleared and data refreshed");
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error("Failed to refresh data");
      console.error("[handleForceRefresh] Error:", error);
    }
  }, [utils, refetch]);

  return (
    <DashboardLayout>
      <div className="space-y-4 md:space-y-6 pb-8">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold mb-2">
                Document Management
              </h1>
              <p className="text-muted-foreground text-sm md:text-base">
                {folderStats?.[activeFolder] || 0} documents in {activeFolder}
                {documents?.filter(d => d.analyzedAt).length ? (
                  <> • {documents.filter(d => d.analyzedAt).length} analyzed</>
                ) : null}
              </p>
              <div className="flex items-center gap-1.5 mt-1.5 text-xs">
                <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                {selectedCompany ? (
                  <span className="text-muted-foreground">
                    Viewing: {selectedCompany.name}
                  </span>
                ) : (
                  <span className="text-amber-500">No company selected</span>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleForceRefresh}
              className="shrink-0"
            >
              Force Refresh
            </Button>
          </div>
        </div>

        {/* Upload Area */}
        <Card className="p-4 md:p-6">
          <div
            className={`border-2 border-dashed rounded-lg p-6 md:p-8 text-center transition-colors ${
              dragActive ? "border-primary bg-primary/5" : "border-border"
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className="h-10 w-10 md:h-12 md:w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-base md:text-lg font-semibold mb-2">
              Drag and drop files here, or click to browse
            </h3>
            <p className="text-xs md:text-sm text-muted-foreground mb-4">
              Supported formats: PDF, images, Word documents, Excel
              spreadsheets, CSV files
            </p>
            <input
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.csv"
            />
            <label htmlFor="file-upload">
              <Button
                variant="outline"
                asChild
                className="min-h-[44px] md:min-h-[36px]"
              >
                <span>Browse Files</span>
              </Button>
            </label>

            {selectedFiles.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium mb-2">
                  {selectedFiles.length} file(s) selected:
                </p>
                <ul className="text-xs md:text-sm text-muted-foreground space-y-1 max-h-32 overflow-y-auto">
                  {selectedFiles.map((file, i) => (
                    <li key={i}>
                      {file.name} ({formatFileSize(file.size)})
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="mt-4 min-h-[44px]"
                >
                  {uploading ? "Uploading..." : "Upload to Inbox"}
                </Button>
                {uploading && (
                  <div className="mt-3 space-y-1">
                    <Progress value={uploadProgress} className="h-2" />
                    <p className="text-xs text-muted-foreground text-right">
                      {uploadProgress}%
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Folder Tabs */}
        <Tabs
          value={activeFolder}
          onValueChange={v => setActiveFolder(v as FolderType)}
        >
          <TabsList
            className={`grid w-full ${isMobile ? "grid-cols-2 gap-2" : "grid-cols-4"}`}
          >
            <TabsTrigger
              value="inbox"
              className="flex items-center gap-1 md:gap-2 text-xs md:text-sm min-h-[44px] md:min-h-[36px]"
            >
              <Inbox className="h-3 w-3 md:h-4 md:w-4" />
              {!isMobile && "Inbox"}
              {folderStats?.inbox ? ` (${folderStats.inbox})` : ""}
            </TabsTrigger>
            <TabsTrigger
              value="processing"
              className="flex items-center gap-1 md:gap-2 text-xs md:text-sm min-h-[44px] md:min-h-[36px]"
            >
              <Clock className="h-3 w-3 md:h-4 md:w-4" />
              {!isMobile && "Processing"}
              {folderStats?.processing ? ` (${folderStats.processing})` : ""}
            </TabsTrigger>
            <TabsTrigger
              value="archived"
              className="flex items-center gap-1 md:gap-2 text-xs md:text-sm min-h-[44px] md:min-h-[36px]"
            >
              <Archive className="h-3 w-3 md:h-4 md:w-4" />
              {!isMobile && "Archived"}
              {folderStats?.archived ? ` (${folderStats.archived})` : ""}
            </TabsTrigger>
            <TabsTrigger
              value="deleted"
              className="flex items-center gap-1 md:gap-2 text-xs md:text-sm min-h-[44px] md:min-h-[36px]"
            >
              <Trash2 className="h-3 w-3 md:h-4 md:w-4" />
              {!isMobile && "Deleted"}
              {folderStats?.deleted ? ` (${folderStats.deleted})` : ""}
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeFolder} className="mt-4 md:mt-6">
            {/* Filters */}
            <div className="mb-4 space-y-3">
              <div className="flex flex-wrap gap-2">
                <select
                  value={filters.type || "all"}
                  onChange={e =>
                    setFilters({ ...filters, type: e.target.value })
                  }
                  className="px-3 py-2 rounded-md border border-border bg-background text-sm min-h-[36px]"
                >
                  <option value="all">All Types</option>
                  <option value="invoice">Invoice</option>
                  <option value="receipt">Receipt</option>
                  <option value="contract">Contract</option>
                  <option value="bank_statement">Bank Statement</option>
                  <option value="tax_form">Tax Form</option>
                  <option value="unknown">Unknown</option>
                </select>

                <select
                  value={filters.size || "all"}
                  onChange={e =>
                    setFilters({ ...filters, size: e.target.value })
                  }
                  className="px-3 py-2 rounded-md border border-border bg-background text-sm min-h-[36px]"
                >
                  <option value="all">All Sizes</option>
                  <option value="small">&lt;100KB</option>
                  <option value="medium">100KB-1MB</option>
                  <option value="large">&gt;1MB</option>
                </select>

                <select
                  value={filters.date || "all"}
                  onChange={e =>
                    setFilters({ ...filters, date: e.target.value })
                  }
                  className="px-3 py-2 rounded-md border border-border bg-background text-sm min-h-[36px]"
                >
                  <option value="all">Any Date</option>
                  <option value="today">Today</option>
                  <option value="7days">Last 7 days</option>
                  <option value="30days">Last 30 days</option>
                  <option value="90days">Last 90 days</option>
                </select>

                <Input
                  placeholder="Search filenames..."
                  value={filters.search || ""}
                  onChange={e =>
                    setFilters({ ...filters, search: e.target.value })
                  }
                  className="flex-1 min-w-[200px] min-h-[36px]"
                />
              </div>

              {/* Active filter chips */}
              {activeFilters.length > 0 && (
                <div className="flex flex-wrap gap-2 items-center">
                  {activeFilters.map(([key, value]) => (
                    <Badge key={key} variant="secondary" className="gap-1">
                      {key}: {value}
                      <X
                        className="h-3 w-3 cursor-pointer"
                        onClick={() =>
                          setFilters({ ...filters, [key]: undefined })
                        }
                      />
                    </Badge>
                  ))}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFilters({})}
                    className="h-6 text-xs"
                  >
                    Clear all
                  </Button>
                </div>
              )}
            </div>

            {/* Batch Action Toolbar */}
            {selectedDocIds.size > 0 && (
              <Card className="p-4 mb-4 sticky top-16 z-20 animate-in slide-in-from-top-5 shadow-lg">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    <span className="font-semibold">
                      {selectedDocIds.size} document
                      {selectedDocIds.size > 1 ? "s" : ""} selected
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedDocIds(new Set())}
                      className="h-7 text-xs"
                    >
                      Clear Selection
                    </Button>
                  </div>

                  <div className="flex items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="default" size="sm">
                          Move to
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {activeFolder !== "inbox" && (
                          <DropdownMenuItem
                            onClick={() => handleBatchMove("inbox")}
                          >
                            <Inbox className="h-4 w-4 mr-2" />
                            Inbox
                          </DropdownMenuItem>
                        )}
                        {activeFolder !== "processing" && (
                          <DropdownMenuItem
                            onClick={() => handleBatchMove("processing")}
                          >
                            <Clock className="h-4 w-4 mr-2" />
                            Processing
                          </DropdownMenuItem>
                        )}
                        {activeFolder !== "archived" && (
                          <DropdownMenuItem
                            onClick={() => handleBatchMove("archived")}
                          >
                            <Archive className="h-4 w-4 mr-2" />
                            Archived
                          </DropdownMenuItem>
                        )}
                        {activeFolder !== "deleted" && (
                          <DropdownMenuItem
                            onClick={() => handleBatchMove("deleted")}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Deleted
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>

                    {activeFolder !== "archived" && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleBatchMove("archived")}
                      >
                        <Archive className="h-4 w-4 mr-2" />
                        Archive
                      </Button>
                    )}

                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleBatchDelete}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Table */}
            {isLoading ? (
              <div className="border rounded-lg overflow-hidden">
                <div className="divide-y">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3 p-4">
                      <Skeleton className="h-4 w-4 shrink-0" />
                      <Skeleton className="h-8 w-8 rounded shrink-0" />
                      <div className="flex-1 space-y-2 min-w-0">
                        <Skeleton className="h-4 w-48" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                      <Skeleton className="h-5 w-20 rounded-full" />
                      <Skeleton className="h-3 w-16" />
                      <Skeleton className="h-8 w-8 rounded" />
                    </div>
                  ))}
                </div>
              </div>
            ) : !filteredAndSortedDocs || filteredAndSortedDocs.length === 0 ? (
              <Card className="p-8 md:p-12 text-center">
                <FileText className="h-10 w-10 md:h-12 md:w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-base md:text-lg font-semibold mb-2">
                  No documents{" "}
                  {activeFilters.length > 0
                    ? "match filters"
                    : `in ${activeFolder}`}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {activeFilters.length > 0 ? (
                    <Button variant="outline" onClick={() => setFilters({})}>
                      Clear Filters
                    </Button>
                  ) : activeFolder === "inbox" ? (
                    "Upload files to get started"
                  ) : (
                    `Move documents to ${activeFolder} to see them here`
                  )}
                </p>
              </Card>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-12">
                        <Checkbox
                          checked={
                            selectedDocIds.size > 0 &&
                            selectedDocIds.size === filteredAndSortedDocs.length
                          }
                          onCheckedChange={toggleSelectAll}
                        />
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none min-w-[280px]"
                        onClick={() => handleSort("name")}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium uppercase tracking-wide">
                            File Details
                          </span>
                          {getSortIcon("name")}
                        </div>
                      </TableHead>
                      <TableHead className="min-w-[200px]">
                        <span className="text-xs font-medium uppercase tracking-wide">
                          Smart Detection
                        </span>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none w-[100px]"
                        onClick={() => handleSort("type")}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium uppercase tracking-wide">
                            Type
                          </span>
                          {getSortIcon("type")}
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer select-none w-[180px]"
                        onClick={() => handleSort("date")}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium uppercase tracking-wide">
                            Metadata
                          </span>
                          {getSortIcon("date")}
                        </div>
                      </TableHead>
                      <TableHead className="w-[120px]">
                        <span className="text-xs font-medium uppercase tracking-wide">
                          Uploader
                        </span>
                      </TableHead>
                      <TableHead className="w-[80px]">
                        <span className="text-xs font-medium uppercase tracking-wide">
                          Actions
                        </span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAndSortedDocs.map((doc, index) => (
                      <TableRow
                        key={doc.id}
                        className={`cursor-pointer ${
                          selectedDocIds.has(doc.id)
                            ? "bg-primary/10 border-l-4 border-primary"
                            : index % 2 === 0
                              ? "bg-background"
                              : "bg-muted/30"
                        } hover:bg-accent/5`}
                        onClick={e => {
                          if (
                            (e.target as HTMLElement).closest(
                              'button, a, [role="button"]'
                            )
                          )
                            return;
                          toggleSelect(doc.id);
                        }}
                      >
                        <TableCell onClick={e => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedDocIds.has(doc.id)}
                            onCheckedChange={() => toggleSelect(doc.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-start gap-2">
                            {getFileIcon(doc.mimeType)}
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-base truncate">
                                {doc.originalName || doc.fileName}
                              </p>
                              {doc.originalName &&
                                doc.originalName !== doc.fileName && (
                                  <p className="text-xs text-muted-foreground truncate">
                                    {doc.fileName}
                                  </p>
                                )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {doc.status === "error" ||
                            doc.status === "processing" ? (
                              <StatusBadge
                                status={doc.status}
                                className="text-xs"
                              />
                            ) : doc.documentType ? (
                              <>
                                <Badge
                                  className={`${getDocumentTypeColor(
                                    doc.documentType
                                  )} text-white text-xs`}
                                >
                                  {doc.documentType.replace("_", " ")}
                                </Badge>
                                {doc.extractedAmount && (
                                  <Badge
                                    variant="secondary"
                                    className="text-xs"
                                  >
                                    CHF {(doc.extractedAmount / 100).toFixed(2)}
                                  </Badge>
                                )}
                                {doc.extractedVendorName && (
                                  <Badge className="bg-accent text-xs">
                                    {doc.extractedVendorName}
                                  </Badge>
                                )}
                              </>
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                Not analyzed
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {doc.mimeType.split("/")[1]?.toUpperCase() ||
                              "Unknown"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="text-sm">
                              {formatDate(doc.uploadedAt.getTime())}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatFileSize(doc.fileSize)}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm truncate">
                            {user?.name || "You"}
                          </p>
                        </TableCell>
                        <TableCell onClick={e => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            {doc.status === "error" ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleAnalyze(doc.id)}
                                className="h-9 w-9 p-0"
                                aria-label="Re-analyze document"
                              >
                                <Sparkles className="h-4 w-4" />
                              </Button>
                            ) : !doc.analyzedAt ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleAnalyze(doc.id)}
                                className="h-9 w-9 p-0"
                                aria-label="Analyze document"
                              >
                                <Sparkles className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSelectedDocForAnalysis(doc.id);
                                  setAnalysisModalOpen(true);
                                }}
                                className="h-9 w-9 p-0"
                                aria-label="View analysis"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-9 w-9 p-0"
                                  aria-label="Document options"
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem>
                                  <Download className="h-4 w-4 mr-2" />
                                  Download
                                </DropdownMenuItem>
                                {doc.mimeType === "text/csv" && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setCsvPreviewDoc({
                                        id: doc.id,
                                        url: doc.fileUrl,
                                        filename: doc.fileName,
                                      });
                                      setCsvPreviewOpen(true);
                                    }}
                                  >
                                    <Eye className="h-4 w-4 mr-2" />
                                    Preview CSV
                                  </DropdownMenuItem>
                                )}
                                {activeFolder !== "processing" && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      moveToFolder.mutate({
                                        documentId: doc.id,
                                        companyId: selectedCompanyId!,
                                        targetFolder: "processing",
                                      })
                                    }
                                  >
                                    <Clock className="h-4 w-4 mr-2" />
                                    Move to Processing
                                  </DropdownMenuItem>
                                )}
                                {activeFolder !== "archived" && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      moveToFolder.mutate({
                                        documentId: doc.id,
                                        companyId: selectedCompanyId!,
                                        targetFolder: "archived",
                                      })
                                    }
                                  >
                                    <Archive className="h-4 w-4 mr-2" />
                                    Move to Archived
                                  </DropdownMenuItem>
                                )}
                                {activeFolder !== "deleted" && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      moveToFolder.mutate({
                                        documentId: doc.id,
                                        companyId: selectedCompanyId!,
                                        targetFolder: "deleted",
                                      })
                                    }
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Move to Deleted
                                  </DropdownMenuItem>
                                )}
                                {activeFolder === "deleted" && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      deletePermanently.mutate({
                                        documentId: doc.id,
                                        companyId: selectedCompanyId!,
                                      })
                                    }
                                    className="text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete Permanently
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Analysis Review Modal */}
        <AnalysisReviewModal
          open={analysisModalOpen}
          onOpenChange={setAnalysisModalOpen}
          documentName={
            documents?.find(d => d.id === selectedDocForAnalysis)?.fileName ||
            ""
          }
          analysis={
            analysisData
              ? {
                  documentType:
                    documents?.find(d => d.id === selectedDocForAnalysis)
                      ?.documentType || "unknown",
                  confidence: analysisData.overallConfidence,
                  vendorCandidates: analysisData.vendorCandidates,
                  transactionCandidates: analysisData.transactionCandidates,
                }
              : null
          }
          onCreateVendor={vendor => {
            if (!selectedCompanyId) {
              toast.error("Please select a company first");
              return;
            }
            createVendor.mutate({
              companyId: selectedCompanyId,
              name: vendor.name || "Unknown Vendor",
              category: vendor.category,
              vendorType: vendor.vendorType as any,
              swissVendor: vendor.swissVendor,
              uid: vendor.uid,
              vatNumber: vendor.vatNumber,
              address: vendor.address,
            });
          }}
          onCreateTransaction={transaction => {
            if (!selectedCompanyId) {
              toast.error("Please select a company first");
              return;
            }
            createTransaction.mutate({
              companyId: selectedCompanyId,
              date: transaction.date || Date.now(),
              description:
                transaction.description || "Transaction from document",
              amount: Math.round((transaction.amount || 0) * 100),
              currency: transaction.currency || "CHF",
              type: transaction.type || "debit",
              category: transaction.category || "Operating Expenses",
              accountCode: transaction.accountCode || "6000",
              accountName: transaction.accountName || "Operating Expenses",
              vatRate: transaction.vatRate,
              vatAmount: transaction.vatAmount
                ? Math.round(transaction.vatAmount * 100)
                : undefined,
              reference: transaction.reference,
              notes: transaction.notes,
            });
          }}
        />

        {/* CSV Preview Dialog */}
        <Dialog open={csvPreviewOpen} onOpenChange={setCsvPreviewOpen}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>CSV Preview</DialogTitle>
            </DialogHeader>
            {csvPreviewDoc && (
              <CSVPreview
                url={csvPreviewDoc.url}
                fileName={csvPreviewDoc.filename}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>

      <ConfirmDialog
        open={bulkDeleteDialog !== null}
        title={
          activeFolder === "deleted"
            ? "Permanently Delete Documents"
            : "Delete Documents"
        }
        description={bulkDeleteDialog?.message ?? ""}
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          const ids = bulkDeleteDialog!.ids;
          setBulkDeleteDialog(null);
          executeBatchDelete(ids);
        }}
        onCancel={() => setBulkDeleteDialog(null)}
      />

      {/* Duplicate file warning dialog (gt-c3i34) */}
      <ConfirmDialog
        open={duplicateWarning !== null}
        title="Duplicate File Detected"
        description={
          duplicateWarning
            ? `The following file${duplicateWarning.duplicates.length !== 1 ? "s are" : " is"} already in the inbox:\n${duplicateWarning.duplicates.join(", ")}\n\nUpload anyway?`
            : ""
        }
        confirmLabel="Upload Anyway"
        onConfirm={() => {
          if (duplicateWarning) {
            setSelectedFiles(duplicateWarning.pendingFiles);
            setDuplicateWarning(null);
          }
        }}
        onCancel={() => setDuplicateWarning(null)}
      />
    </DashboardLayout>
  );
}
